import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import {
  Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
  ListOrdered, FileText,
} from 'lucide-react';

const OutlineEditor = ({ outline = [], onChange }) => {
  const update = (newOutline) => onChange(newOutline);

  // Section operations
  const addSection = () => {
    update([...outline, { title: '', items: [], order: outline.length }]);
  };

  const updateSection = (idx, field, value) => {
    const copy = [...outline];
    copy[idx] = { ...copy[idx], [field]: value };
    update(copy);
  };

  const removeSection = (idx) => {
    update(outline.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  };

  const moveSection = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= outline.length) return;
    const copy = [...outline];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    update(copy.map((s, i) => ({ ...s, order: i })));
  };

  // Item operations
  const addItem = (sectionIdx) => {
    const copy = [...outline];
    const items = [...(copy[sectionIdx].items || [])];
    items.push({ title: '', description: '', duration: '', order: items.length });
    copy[sectionIdx] = { ...copy[sectionIdx], items };
    update(copy);
  };

  const updateItem = (sectionIdx, itemIdx, field, value) => {
    const copy = [...outline];
    const items = [...copy[sectionIdx].items];
    items[itemIdx] = { ...items[itemIdx], [field]: value };
    copy[sectionIdx] = { ...copy[sectionIdx], items };
    update(copy);
  };

  const removeItem = (sectionIdx, itemIdx) => {
    const copy = [...outline];
    const items = copy[sectionIdx].items.filter((_, i) => i !== itemIdx).map((it, i) => ({ ...it, order: i }));
    copy[sectionIdx] = { ...copy[sectionIdx], items };
    update(copy);
  };

  const moveItem = (sectionIdx, itemIdx, dir) => {
    const copy = [...outline];
    const items = [...copy[sectionIdx].items];
    const target = itemIdx + dir;
    if (target < 0 || target >= items.length) return;
    [items[itemIdx], items[target]] = [items[target], items[itemIdx]];
    copy[sectionIdx] = { ...copy[sectionIdx], items: items.map((it, i) => ({ ...it, order: i })) };
    update(copy);
  };

  return (
    <div data-testid="outline-editor" className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold flex items-center gap-2">
          <ListOrdered className="w-4 h-4 text-[#F9B438]" />
          Programme de formation ({outline.length} section{outline.length !== 1 ? 's' : ''})
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addSection}
          data-testid="add-section-btn"
        >
          <Plus className="w-3 h-3 mr-1" />Section
        </Button>
      </div>

      {outline.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-neutral-200 rounded-lg">
          <FileText className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
          <p className="text-sm text-neutral-400">Aucune section dans le programme.</p>
          <Button type="button" variant="ghost" size="sm" onClick={addSection} className="mt-2">
            <Plus className="w-3 h-3 mr-1" />Ajouter une section
          </Button>
        </div>
      )}

      {outline.map((section, sIdx) => (
        <Card key={sIdx} className="border-l-4 border-l-[#F9B438]" data-testid={`outline-section-${sIdx}`}>
          <CardContent className="p-4 space-y-3">
            {/* Section Header */}
            <div className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-neutral-300 flex-shrink-0" />
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-[#1D1D1B] flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}>
                {sIdx + 1}
              </span>
              <Input
                value={section.title}
                onChange={e => updateSection(sIdx, 'title', e.target.value)}
                placeholder="Titre de la section (ex: Jour 1 - Introduction)"
                className="flex-1 font-medium"
                data-testid={`section-title-${sIdx}`}
              />
              <div className="flex gap-0.5 flex-shrink-0">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8"
                  onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0}>
                  <ChevronUp className="w-3 h-3" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8"
                  onClick={() => moveSection(sIdx, 1)} disabled={sIdx === outline.length - 1}>
                  <ChevronDown className="w-3 h-3" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                  onClick={() => removeSection(sIdx)} data-testid={`remove-section-${sIdx}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Items */}
            <div className="ml-9 space-y-2">
              {(section.items || []).map((item, iIdx) => (
                <div key={iIdx} className="flex gap-2 items-start bg-neutral-50 rounded-lg p-2.5"
                  data-testid={`outline-item-${sIdx}-${iIdx}`}>
                  <span className="text-xs text-neutral-400 font-mono mt-2.5 w-5 text-center flex-shrink-0">
                    {sIdx + 1}.{iIdx + 1}
                  </span>
                  <div className="flex-1 space-y-1.5">
                    <Input
                      value={item.title}
                      onChange={e => updateItem(sIdx, iIdx, 'title', e.target.value)}
                      placeholder="Titre du module"
                      className="h-8 text-sm"
                      data-testid={`item-title-${sIdx}-${iIdx}`}
                    />
                    <div className="flex gap-2">
                      <Input
                        value={item.description || ''}
                        onChange={e => updateItem(sIdx, iIdx, 'description', e.target.value)}
                        placeholder="Description (optionnel)"
                        className="h-7 text-xs flex-1"
                      />
                      <Input
                        value={item.duration || ''}
                        onChange={e => updateItem(sIdx, iIdx, 'duration', e.target.value)}
                        placeholder="Duree"
                        className="h-7 text-xs w-24"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                      onClick={() => moveItem(sIdx, iIdx, -1)} disabled={iIdx === 0}>
                      <ChevronUp className="w-3 h-3" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                      onClick={() => moveItem(sIdx, iIdx, 1)} disabled={iIdx === (section.items || []).length - 1}>
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600"
                      onClick={() => removeItem(sIdx, iIdx)} data-testid={`remove-item-${sIdx}-${iIdx}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}

              <Button type="button" variant="ghost" size="sm"
                className="text-xs text-neutral-500 hover:text-[#F9B438]"
                onClick={() => addItem(sIdx)}
                data-testid={`add-item-btn-${sIdx}`}>
                <Plus className="w-3 h-3 mr-1" />Ajouter un module
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default OutlineEditor;
